/**
 * SAM-1298 — Support › Business Requests (canvas S1–S7): the inbox rows, the chips, who decides what and the
 * detail's fields. Pure, so it is unit-tested; the pages only render it.
 */
import type { BusinessRequest, RequestOptions, RequestType, RequestUiStatus } from '../../api/businessRequestsApi';
import type { Tone } from '../BusinessPage/activity';
import { formatDay } from '../../utils/dateUtils';

export const businessRequestKeys = {
  list: ['business-requests'] as const,
  options: ['business-requests', 'options'] as const,
};

export const TYPE_LABEL: Record<RequestType, string> = {
  business: 'Business', service: 'Service', employee: 'Employee', ad: 'Ad',
};

/** v1: Event keeps its own draft → publish flow (Events › Review), so it has no chip here. */
export const TYPES: RequestType[] = ['business', 'service', 'employee', 'ad'];
export type Chip = 'all' | RequestType;

/** The words of C6/C7: Processing · Approved · Live · Denied (the server's `ui_status`). */
export const STATUS_VIEW: Record<RequestUiStatus, { label: string; tone: Tone }> = {
  processing: { label: 'Processing', tone: 'wait' },
  approved: { label: 'Approved', tone: 'ok' },
  live: { label: 'Live', tone: 'ok' },
  denied: { label: 'Denied', tone: 'bad' },
  withdrawn: { label: 'Withdrawn', tone: 'off' },
};

/** A status the screen does not know yet (or a placeholder row) reads as itself, in grey. */
export const statusView = (status: string | null | undefined): { label: string; tone: Tone } =>
  STATUS_VIEW[status as RequestUiStatus] ?? { label: status ?? '', tone: 'off' };

/** Still waiting for the tenant: the selector is shown. `approving` is the server's transient state. */
export const isOpen = (r: Pick<BusinessRequest, 'status'>): boolean => r.status === 'sent' || r.status === 'approving';

/** A timestamp as the day it fell on here, month written out: «Oct 30, 2026». */
export const dayOf = (ts: string | null | undefined): string => {
  if (!ts) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(ts)) return formatDay(ts);
  const d = new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return formatDay(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`);
};

export interface InboxRow {
  key: string;
  type: RequestType;
  /** A Business request of the Estate category (it asks to administer an estate group). */
  estate: boolean;
  business: string;
  requestedBy: string;
  summary: string;
  sentAt: string | null;
  uiStatus: RequestUiStatus;
  /** Detail route. */
  href: string;
}

const text = (v: unknown): string => (typeof v === 'string' ? v.trim() : typeof v === 'number' ? String(v) : '');

/** «New business», «New business · Estate · 7 Mares», «Edit · Private table», «Carnet · Laura Méndez». */
export function requestSummary(r: BusinessRequest, groupName: (id: number | null) => string = () => ''): string {
  const title = text(r.title);
  if (r.kind === 'edit') return ['Edit', title].filter(Boolean).join(' · ');
  switch (r.type) {
    case 'business': return r.is_estate ? ['New business · Estate', groupName(r.estate_group_id)].filter(Boolean).join(' · ') : 'New business';
    case 'employee': return ['Carnet', title].filter(Boolean).join(' · ');
    default: return title;
  }
}

export function requestRows(requests: BusinessRequest[], groupName?: (id: number | null) => string): InboxRow[] {
  return requests.map((r) => ({
    key: r.id,
    type: r.type,
    estate: r.type === 'business' && r.is_estate,
    business: r.business_name || (r.type === 'business' ? text(r.title) : ''),
    requestedBy: r.submitted_by?.name || r.submitted_by?.email || '',
    summary: requestSummary(r, groupName),
    sentAt: r.sent_at,
    uiStatus: r.ui_status,
    href: `/support/business-requests/${encodeURIComponent(r.id)}`,
  }));
}

/** The estate group's name from /options (the request carries only its id). */
export const groupNamer = (options: RequestOptions | undefined) => (id: number | null): string =>
  (id == null ? '' : options?.estate_groups.find((g) => g.id === id)?.name ?? '');

export function chipCounts(rows: InboxRow[]): Record<Chip, number> {
  const c: Record<Chip, number> = { all: rows.length, business: 0, service: 0, employee: 0, ad: 0 };
  rows.forEach((r) => { if (r.type in c) c[r.type] += 1; });
  return c;
}

export function filterRows(rows: InboxRow[], chip: Chip, query: string): InboxRow[] {
  const q = query.trim().toLowerCase();
  return rows.filter((r) => (chip === 'all' || r.type === chip)
    && (!q || `${r.business} ${r.requestedBy} ${r.summary}`.toLowerCase().includes(q)));
}

type Who = { isAdmin: boolean; isTenantFrontDesk: boolean };

/**
 * What the inbox lists (Rodrigo, 30 Oct + v1 cut 2 Nov): the tenant admin and Xhinola (`isAdmin`) every type; the
 * tenant Front Desk only Employee. The server answers with the same rule; this only shapes the screen.
 */
export const visibleTypes = (who: Who): RequestType[] =>
  who.isAdmin ? TYPES : who.isTenantFrontDesk ? ['employee'] : [];

/** Who may answer a request. An Estate request gives power over residents: only `admin` / `xhinola_admin`. */
export function canDecide(who: Who, type: RequestType): boolean {
  if (type === 'employee') return who.isAdmin || who.isTenantFrontDesk;
  return who.isAdmin;
}

/* ---- Detail ---- */

/** Payload fields the detail shows, by type, in this order (contract keys). Anything else is not shown. */
const FIELDS: Record<RequestType, Array<[string, string]>> = {
  business: [
    ['name', 'Name'], ['description_en', 'Description'], ['description_es', 'Description (ES)'], ['email', 'Email'],
    ['phone_number', 'Phone'], ['website_url', 'Website'], ['instagram_url', 'Instagram'], ['address', 'Address'],
  ],
  service: [
    ['name_en', 'Name'], ['name_es', 'Name (ES)'], ['price', 'Price'], ['duration', 'Duration'], ['capacity', 'Capacity'],
    ['description_en', 'Description'], ['description_es', 'Description (ES)'],
  ],
  ad: [
    ['title_en', 'Title'], ['title_es', 'Title (ES)'], ['short_description_en', 'Text'], ['short_description_es', 'Text (ES)'],
    ['publish_date', 'Publish'], ['expiration_date', 'Ends'],
  ],
  employee: [
    ['phone', 'Phone'], ['birthdate', 'Date of birth'], ['position_key', 'Position'], ['valid_until', 'Valid until'],
  ],
};

const DATE_FIELDS = new Set(['publish_date', 'expiration_date', 'birthdate', 'valid_until']);

export const fieldValue = (key: string, v: unknown, payload?: Record<string, unknown> | null): string => {
  if (DATE_FIELDS.has(key)) return dayOf(text(v).slice(0, 19)) || text(v);
  if (key === 'price' && (typeof v === 'number' || typeof v === 'string') && text(payload?.currency)) return `${text(payload?.currency)} ${v}`;
  return typeof v === 'boolean' ? (v ? 'Yes' : 'No') : text(v);
};

export function detailFields(type: RequestType, payload: Record<string, unknown> | null | undefined): Array<{ label: string; value: string }> {
  const p = payload ?? {};
  return (FIELDS[type] ?? [])
    .filter(([k]) => k in p)
    .map(([k, label]) => ({ label, value: fieldValue(k, p[k], p) }))
    .filter((f) => f.value);
}

/** An edit (S7): before → after for each changed field the screen knows. */
export function changedFields(r: BusinessRequest): Array<{ label: string; before: string; after: string }> {
  const p = r.payload ?? {};
  return (FIELDS[r.type] ?? [])
    .filter(([k]) => k in p)
    .map(([k, label]) => ({ label, before: fieldValue(k, r.current?.[k], r.current), after: fieldValue(k, p[k], p) }));
}

/** The line under the status selector (canvas S2–S7). */
export function decisionHint(r: Pick<BusinessRequest, 'type' | 'kind' | 'payload' | 'submitted_by' | 'is_estate'>, groupName = ''): string {
  if (r.kind === 'edit') return 'The live version stays as it is until you approve. Approved replaces it.';
  const who = r.submitted_by?.name || r.submitted_by?.email || 'the requester';
  switch (r.type) {
    case 'business':
      return r.is_estate
        ? `Approved activates the business and makes ${who} the administrator of ${groupName || 'the estate group'}.`
        : `Approved activates the business and gives ${who} the Owner role. It shows in the app once it has a photo, hours and 1 live service.`;
    case 'service': return 'Approved makes the service bookable in the app.';
    case 'ad': {
      const day = dayOf(text(r.payload?.publish_date).slice(0, 19));
      return `Approved publishes the ad${day ? ` on ${day}` : ''}.`;
    }
    case 'employee': return 'Approved issues the carnet.';
    default: return '';
  }
}

/** The server's refusals in the screen's words (contract error codes); anything else, its own message. */
export function decisionErrorText(error: unknown): string {
  const data = (error as { response?: { data?: { code?: string; error?: string } } } | null)?.response?.data;
  switch (data?.code) {
    case 'estate_group_has_admin': return 'This estate group already has an administrator.';
    case 'resident_account': return 'This account is a resident. The owner needs another account.';
    case 'estate_approver_required': return 'Only the tenant Admin or Xhinola can approve an Estate request.';
    case 'not_pending': return 'This request was already answered.';
    case 'target_changed': return 'What this edit changes no longer exists or moved to another business.';
    case 'target_rejected': return data.error || 'The request could not be applied.';
    case 'account_missing': return 'The account that sent this request no longer exists.';
    default: return data?.error || 'The status could not be updated. Try again.';
  }
}
